import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, FileText, ExternalLink } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';
import api from '../services/api';

const InsightsPage = () => {
  const [papers, setPapers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPapers = async () => {
      try {
        const response = await api.get('/api/papers');
        setPapers(response.data);
      } catch (error) {
        console.error('Error fetching papers:', error);
      }
    };
    fetchPapers();
  }, []);

  const filteredPapers = papers.filter(p => {
    const matchesSearch = !searchQuery || p.title.toLowerCase().includes(searchQuery.toLowerCase()) || (p.abstract && p.abstract.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });

  return (
    <Layout>
      <SEO
        title="Insights and Publications | Sandend House"
        description="Read founder notes, research, media appearances and portfolio perspectives from Calum Knox and Sandend House."
        path="/insights"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Insights",
          "url": "https://theknox.co.uk/insights",
          "isPartOf": { "@id": "https://theknox.co.uk/#website" }
        }}
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 border-b border-[var(--sh-rule-light)]">
        <div className="section-container">
          <p className="eyebrow mb-4">Insights</p>
          <h1 className="font-serif text-4xl lg:text-5xl mb-8" data-testid="insights-heading">
            Ideas, research and perspective.
          </h1>
          <p className="text-[var(--sh-grey)] reading-width">
            Selected writing, research, media appearances and perspectives from Calum Knox, Sandend House and the wider portfolio.
          </p>
        </div>
      </section>

      {/* Search */}
      <section className="py-6 border-b border-[var(--sh-rule-light)] bg-white">
        <div className="section-container">
          <input
            type="text"
            placeholder="Search insights..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full max-w-md px-4 py-2.5 text-sm border border-[var(--sh-rule)] rounded-sm bg-[var(--sh-paper)] text-[var(--sh-charcoal)] placeholder:text-[var(--sh-grey)] focus:outline-none focus:border-[var(--sh-ink)]"
            data-testid="search-input"
          />
        </div>
      </section>

      {/* Articles */}
      <section className="py-16 lg:py-20">
        <div className="section-container">
          {filteredPapers.length > 0 ? (
            <div className="space-y-8">
              {filteredPapers.map((paper) => (
                <article
                  key={paper.id}
                  className="border-b border-[var(--sh-rule-light)] pb-8 last:border-b-0 cursor-pointer group"
                  onClick={() => navigate(`/insights/${paper.id}`)}
                  data-testid={`insight-${paper.id}`}
                >
                  <p className="eyebrow mb-2">{paper.status || 'Publication'}</p>
                  <h2 className="font-serif text-xl lg:text-2xl mb-2 group-hover:text-[var(--sh-brass)] transition-colors">{paper.title}</h2>
                  <p className="text-[var(--sh-grey)] text-sm mb-3">
                    {paper.author} &middot; {paper.publishDate || paper.date}
                  </p>
                  {paper.abstract && (
                    <p className="text-[var(--sh-grey)] text-sm leading-relaxed line-clamp-2">{paper.abstract}</p>
                  )}
                  {paper.tags && paper.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {paper.tags.map((tag, i) => (
                        <span key={i} className="text-xs text-[var(--sh-grey)] border border-[var(--sh-rule)] px-2 py-0.5 rounded-sm">{tag}</span>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-[var(--sh-grey)] text-lg mb-2">No insights published yet.</p>
              <p className="text-[var(--sh-grey)] text-sm">Check back soon for founder notes, research and perspectives.</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default InsightsPage;
